import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useNavigation } from '../context/NavigationContext';
import { useScale, scaleW, scaleH } from '../hooks/useScale';
import { useDynamicBackground } from '../hooks/useDynamicBackground';
import { normalizeLikeCounts } from '../components/shared/hooks/useFeedData';
import {
  ArrowLeft,
  Heart,
  MessageCircle,
  Share2,
  Bookmark,
  MoreHorizontal,
  Edit,
  Trash2,
  Pin,
  UserPlus,
  UserMinus,
  Flag,
  Bell,
  BellOff,
  ThumbsDown,
  ThumbsUp,
  Send,
  X,
  Edit3,
  MoreVertical,
  Trash,
  LogOut,
  ChevronDown,
  Users
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { PostBadges } from '../components/desktop/PostBadges';
import ThemeCustomizerButton from '../components/desktop/ThemeCustomizer/ThemeCustomizerButton';
import SiteThemeCustomizer from '../components/desktop/ThemeCustomizer/SiteThemeCustomizer';
import { EditPostModal } from '../components/desktop/EditPostModal';
import LoadingScreen from '../components/desktop/LoadingScreen';

import { canModeratePost, getEditorType, getPinnerType } from '../utils/permissions';
import PostDetailNavbar from '../components/desktop/PostDetailNavbar';
import { postEventBus } from '../lib/postEventBus';
import { usePostSync } from '../hooks/usePostSync';
import { commentEventBus } from '../lib/commentEventBus';
import { useCommentSync } from '../hooks/useCommentSync';
import { getBackground, getCardBackground, calculateNavbarBg } from '../utils/backgroundHelpers';
import { cacheTheme, getCachedTheme } from '../utils/themeCache';
import { transformColor, type ColorTransformOptions } from '../utils/colorTransform';
import { getThemeAnimations } from '@theme/animation-config';
import resolveAvatar from '../lib/resolveAvatar';

import { fetchWithAuth } from '../lib/fetchWithAuth';
interface Author {
  id: number;
  username: string;
  name?: string;
  profileName?: string;
  profilePicture?: string;
  image?: string;
  bio?: string;
}

interface Community {
  id: string;
  name: string;
  nameSlug: string;
  communityCustomization?: any; // Community theme customization
}

interface Reply {
  id: string;
  content: string;
  createdAt: string;
  author: Author;
  likes: number;
  dislikes: number;
  liked?: boolean;
  disliked?: boolean;
}

interface CommunityPost {
  id: string;
  title?: string;
  slug?: string;
  postType: 'community' | 'forum';
  content: string;
  tags: string[];
  createdAt: string;
  updatedAt?: string;
  editedBy?: number;
  editedByType?: string;
  isPinned?: boolean;
  pinnedByType?: string;
  author: Author;
  community: Community;
  likes: number;
  dislikes: number;
  liked?: boolean;
  disliked?: boolean;
  replies: Reply[];
  comments: number;
  reposts?: number;
}

export default function ForumPostView() {
  const { communitySlug, postSlug } = useParams<{ communitySlug: string; postSlug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading, logout, setShowLoginModal } = useAuth();
  const { theme, currentPreset } = useTheme();
  const { setCurrentTheme } = useNavigation();
  const scale = useScale();
  const backgroundHeight = useDynamicBackground();
  const [themeAnimations, setThemeAnimations] = useState(() => {
    // Try to load animations from cached site theme first
    const cachedSiteTheme = getCachedTheme('site');
    if (cachedSiteTheme?.animations) {
      return cachedSiteTheme.animations;
    }
    // Fallback to preset animations
    return getThemeAnimations(currentPreset);
  });

  const [post, setPost] = useState<CommunityPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'all' | 'following' | 'friends'>('all');
  const [showMenu, setShowMenu] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showSiteCustomizer, setShowSiteCustomizer] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Pagination for replies
  const [repliesPage, setRepliesPage] = useState(1);
  const [repliesTotalPages, setRepliesTotalPages] = useState(1);
  const [repliesLoading, setRepliesLoading] = useState(false);

  // Notifications
  const [notifications, setNotifications] = useState<any[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);

  const menuRef = useRef<HTMLDivElement>(null);
  const [communityCustomization, setCommunityCustomization] = useState<any>(null);

  // Fetch community customization using communitySlug from URL
  useEffect(() => {
    if (communitySlug) {
      fetchWithAuth(`/api/community/slug/${communitySlug}`, {
        credentials: 'include'
      })
        .then(res => res.json())
        .then(data => {
          if (data.community?.communityCustomization) {
            const customization = data.community.communityCustomization;
            // Community uses 'presetTheme' field, not 'preset'
            const communityPreset = customization.presetTheme || customization.preset || 'ocean-turtle';

                        setCommunityCustomization(customization);

            // Set theme animations based on community's preset
            const animations = getThemeAnimations(communityPreset);
                        setThemeAnimations(animations);
          }
        })
        .catch(err => console.error('❌ [ForumPostView] Failed to fetch community:', err));
    }
  }, [communitySlug]);

  // LOCAL THEME: Use community theme from fetched customization, fallback to site theme
  // IMPORTANT: Transform colors when colorSpace is enabled (like CommunityClient does)
  const localTheme = React.useMemo(() => {
    // If we have community customization, use it and transform it
    if (communityCustomization) {
      const customization = communityCustomization;
      
      // Helper to convert hex to rgba with opacity
      const hexToRgbaWithOpacity = (hex: string, opacity: number) => {
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, ${opacity / 100})`;
      };

      const colorSpace = customization.colorSpace || 'Off';

      // If color space is "Off", return raw colors without transformation
      if (colorSpace === 'Off') {
        const navbarBg = customization.navbarBg
          ? hexToRgbaWithOpacity(customization.navbarBg, customization.navbarOpacity ?? 92)
          : hexToRgbaWithOpacity(customization.primaryColor || theme.primaryColor!, customization.navbarOpacity ?? 92);

        const tabBarBg = customization.tabBarBg
          ? hexToRgbaWithOpacity(customization.tabBarBg, customization.tabBarOpacity ?? 88)
          : navbarBg;

        return {
          ...theme,
          textColor: customization.textColor || theme.textColor,
          accentColor: customization.accentColor || customization.primaryColor || theme.accentColor,
          primaryColor: customization.primaryColor || theme.primaryColor,
          secondaryColor: customization.secondaryColor || customization.primaryColor || theme.secondaryColor,
          cardBg: customization.cardBg || navbarBg,
          cardGradientColor1: customization.cardGradientColor1 || theme.cardGradientColor1,
          cardGradientColor2: customization.cardGradientColor2 || theme.cardGradientColor2,
          cardGradientEnabled: customization.cardGradientEnabled ?? theme.cardGradientEnabled,
          navbarBg,
          tabBarBg,
        };
      }

      // Apply color transformations when colorSpace is enabled (TMI/HSV/RGB)
      
      const transformOptions: ColorTransformOptions = {
        colorSpace,
        temperatureLimit: customization.temperatureLimit ?? 50,
        magentaLimit: customization.magentaLimit ?? 50,
        intensityLimit: customization.intensityLimit ?? 50,
        saturationLimit: customization.backgroundSaturationLimit ?? 50,
        brightnessLimit: customization.backgroundBrightnessLimit ?? 50,
        redLimit: customization.redLimit ?? 50,
        greenLimit: customization.greenLimit ?? 50,
        blueLimit: customization.blueLimit ?? 50,
        contrast: customization.backgroundContrast ?? 50,
        hueGravity: customization.backgroundHueGravity ?? 0,
        graySaturation: customization.backgroundGraySaturation ?? 0,
        targetHue: customization.backgroundDefaultHue ?? 184,
      };

      // Transform ALL color properties
      const transformedTextColor = transformColor(customization.textColor || theme.textColor!, transformOptions);
      const transformedAccentColor = transformColor(customization.accentColor || customization.primaryColor || theme.accentColor!, transformOptions);
      const transformedPrimaryColor = transformColor(customization.primaryColor || theme.primaryColor!, transformOptions);
      const transformedSecondaryColor = transformColor(customization.secondaryColor || customization.primaryColor || theme.secondaryColor!, transformOptions);
      const transformedCardBg = customization.cardBg ? transformColor(customization.cardBg, transformOptions) : transformedPrimaryColor;
      const transformedCardGradientColor1 = customization.cardGradientColor1
        ? transformColor(customization.cardGradientColor1, transformOptions)
        : theme.cardGradientColor1;
      const transformedCardGradientColor2 = customization.cardGradientColor2
        ? transformColor(customization.cardGradientColor2, transformOptions)
        : theme.cardGradientColor2;

      // Transform navbar and tab bar colors, then apply opacity
      const transformedNavbarBgColor = customization.navbarBg
        ? transformColor(customization.navbarBg, transformOptions)
        : transformedPrimaryColor;
      const navbarBg = hexToRgbaWithOpacity(transformedNavbarBgColor, customization.navbarOpacity ?? 92);

      const transformedTabBarBgColor = customization.tabBarBg
        ? transformColor(customization.tabBarBg, transformOptions)
        : transformedPrimaryColor;
      const tabBarBg = customization.tabBarBg
        ? hexToRgbaWithOpacity(transformedTabBarBgColor, customization.tabBarOpacity ?? 88)
        : navbarBg;

      
      return {
        ...theme,
        textColor: transformedTextColor,
        accentColor: transformedAccentColor,
        primaryColor: transformedPrimaryColor,
        secondaryColor: transformedSecondaryColor,
        cardBg: transformedCardBg,
        cardGradientColor1: transformedCardGradientColor1,
        cardGradientColor2: transformedCardGradientColor2,
        cardGradientEnabled: customization.cardGradientEnabled ?? theme.cardGradientEnabled,
        navbarBg,
        tabBarBg,
      };
    }

    // Fallback: Use site theme from ThemeContext
        return theme;
  }, [theme, communityCustomization]);

  // STEP 1: Register theme from navigation state IMMEDIATELY (before post loads)
  useEffect(() => {
    const navState = location.state as any;
    if (navState?.themeContext?.colors) {
      setCurrentTheme({
        type: navState.themeContext.type,
        communityId: navState.themeContext.communityId,
        colors: navState.themeContext.colors
      });
          }
  }, [location.state, setCurrentTheme]);

  // STEP 2: Re-register when post loads with actual community data
  useEffect(() => {
    if (!post?.community?.id || !localTheme.primaryColor) return;

    setCurrentTheme({
      type: 'community',
      communityId: post.community.id,
      colors: {
        primaryColor: localTheme.primaryColor,
        secondaryColor: localTheme.secondaryColor || localTheme.primaryColor,
        textColor: localTheme.textColor || '#ffffff',
        accentColor: localTheme.accentColor || localTheme.primaryColor
      }
    });

      }, [post?.community?.id, localTheme.primaryColor, localTheme.secondaryColor, localTheme.textColor, localTheme.accentColor, setCurrentTheme]);

  // Cache the transformed community theme to sessionStorage for loading screen
  useEffect(() => {
    if (!post?.community?.id) return;

    // Cache the transformed theme for this community
    cacheTheme({
      textColor: localTheme.textColor!,
      accentColor: localTheme.accentColor!,
      cardBg: localTheme.cardBg,
      appBg: getBackground(localTheme as any, communityCustomization || {}, theme.appBg),
      navbarBg: localTheme.navbarBg || '',
      tabBarBg: localTheme.tabBarBg || '',
      primaryColor: localTheme.primaryColor!,
      secondaryColor: localTheme.secondaryColor!,
      colorSpace: 'Off'
    }, 'community', post.community.id);
  }, [localTheme, post?.community?.id, theme.appBg]);

  const themeColors = {
    navbarBg: calculateNavbarBg(localTheme.primaryColor!, 92),
    accentColor: localTheme.accentColor!,
    textColor: localTheme.textColor!,
    tabBarBg: calculateNavbarBg(localTheme.primaryColor!, 92),
    gradientFrom: localTheme.primaryColor!,
    gradientTo: localTheme.secondaryColor!
  };

  // Listen for post updates from other pages/components
  usePostSync({
    onLike: (data) => {
      if (post && post.id === data.postId) {
        setPost(prev => prev ? { ...prev, liked: data.liked, likes: data.likes } : null);
      }
    },
    onDislike: (data) => {
      if (post && post.id === data.postId) {
        setPost(prev => prev ? { ...prev, disliked: data.disliked, dislikes: data.dislikes } : null);
      }
    },
    postIds: post ? [post.id] : undefined
  });

  // Listen for comment updates from other pages/components
  useCommentSync({
    onLike: (data) => {
      setPost(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          replies: prev.replies.map((r: any) =>
            r.id === data.commentId.toString()
              ? { ...r, liked: data.liked, likes: data.likes }
              : r
          ),
        };
      });
    },
    onDislike: (data) => {
      setPost(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          replies: prev.replies.map((r: any) =>
            r.id === data.commentId.toString()
              ? { ...r, disliked: data.disliked, dislikes: data.dislikes }
              : r
          ),
        };
      });
    },
  });

  const handleLogout = async () => {
    logout();
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchPost = useCallback(async () => {
    if (!communitySlug || !postSlug) return;

    // Wait for auth to finish loading before setting auth state
    if (authLoading) {
      return;
    }

    try {

      // Check if user is authenticated
      if (user) {
        setIsAuthenticated(true);
      } else {
        setIsAuthenticated(false);
      }

      const response = await fetchWithAuth(`/api/communities/slug/${communitySlug}/forum/${postSlug}`, {
        credentials: 'include'
      });

      if (!response.ok) {
        if (response.status === 404) {
          setPost(null);
          return;
        }
        throw new Error('Failed to fetch post');
      }

      const data = await response.json();
      setPost(normalizeLikeCounts(data.post));

      // Community theme is already loaded by ThemeContext via NavigationContext
      // No need to manually fetch and transform colors here
    } catch (error) {
      setPost(null);
    } finally {
      setLoading(false);
    }
  }, [communitySlug, postSlug, user, authLoading]);

  const fetchReplies = useCallback(async (page: number = 1) => {
    if (!post) return;

    setRepliesLoading(true);
    try {
      const sortOrder = post.postType === 'forum' ? 'asc' : 'desc';
      const response = await fetchWithAuth(`/api/communities/${post.community.id}/posts/${post.id}/replies?page=${page}&limit=20&sortOrder=${sortOrder}`,
        {
          credentials: 'include'
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch replies');
      }

      const data = await response.json();
      setPost(prev => prev ? {
        ...prev,
        replies: data.replies
      } : null);
      setRepliesPage(data.pagination.page);
      setRepliesTotalPages(data.pagination.totalPages);
    } catch (error) {
    } finally {
      setRepliesLoading(false);
    }
  }, [post]);

  useEffect(() => {
    fetchPost();
  }, [fetchPost]);

  useEffect(() => {
    if (post) {
      fetchReplies(1);
    }
  }, [post?.id]);

  useEffect(() => {
    if (post && user) {
      checkFollowingStatus();
    }
  }, [post, user]);

  // Fetch notifications on mount
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await fetchWithAuth('/api/notifications', {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setNotifications(data.notifications || []);
          setUnreadCount(data.unreadCount || 0);
        }
      } catch (error) {
      }
    };

    fetchNotifications();
  }, []);

  // Theme updates are handled automatically by ThemeContext

  const checkFollowingStatus = async () => {
    if (!post || !user) return;
    try {
      const response = await fetchWithAuth(`/api/profile/username/${post.author.username}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setIsFollowing(data.isFollowing || false);
      }
    } catch (error) {
    }
  };

  const handleLike = async () => {
    if (!post) return;
    if (!user) {
      setShowLoginModal(true);
      return;
    }

    // Capture initial state before any updates
    const wasLiked = post.liked || false;
    const initialLikes = post.likes;
    const newLiked = !wasLiked;
    const newLikes = wasLiked ? initialLikes - 1 : initialLikes + 1;

    // Optimistic update
    setPost(prev => prev ? { ...prev, liked: newLiked, likes: newLikes } : null);

    // Emit local event for same-page sync
    postEventBus.emitLike(post.id, newLiked, newLikes);

    try {
      const response = await fetchWithAuth(`/api/communities/${post.community.id}/posts/${post.id}/like`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      });

      if (!response.ok) {
        // Revert to initial state on error
        setPost(prev => prev ? { ...prev, liked: wasLiked, likes: initialLikes } : null);
        // Emit revert event
        postEventBus.emitLike(post.id, wasLiked, initialLikes);
      }
    } catch (error) {
      // Revert to initial state on error
      setPost(prev => prev ? { ...prev, liked: wasLiked, likes: initialLikes } : null);
      // Emit revert event
      postEventBus.emitLike(post.id, wasLiked, initialLikes);
    }
  };

  const handleDislike = async () => {
    if (!post) return;
    if (!user) {
      setShowLoginModal(true);
      return;
    }

    // Capture initial state before any updates
    const wasDisliked = post.disliked || false;
    const initialDislikes = post.dislikes;
    const newDisliked = !wasDisliked;
    const newDislikes = wasDisliked ? initialDislikes - 1 : initialDislikes + 1;

    // Optimistic update
    setPost(prev => prev ? { ...prev, disliked: newDisliked, dislikes: newDislikes } : null);

    // Emit local event for same-page sync
    postEventBus.emitDislike(post.id, newDisliked, newDislikes);

    try {
      const response = await fetchWithAuth(`/api/communities/${post.community.id}/posts/${post.id}/dislike`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      });

      if (!response.ok) {
        // Revert to initial state on error
        setPost(prev => prev ? { ...prev, disliked: wasDisliked, dislikes: initialDislikes } : null);
        // Emit revert event
        postEventBus.emitDislike(post.id, wasDisliked, initialDislikes);
      }
    } catch (error) {
      // Revert to initial state on error
      setPost(prev => prev ? { ...prev, disliked: wasDisliked, dislikes: initialDislikes } : null);
      // Emit revert event
      postEventBus.emitDislike(post.id, wasDisliked, initialDislikes);
    }
  };

  const handleCommentLike = useCallback(async (replyId: string) => {
    if (!post) return;
    if (!user) {
      setShowLoginModal(true);
      return;
    }

    // Find the reply to capture initial state
    const targetReply = post.replies.find(r => r.id === replyId);
    if (!targetReply) return;

    const wasLiked = targetReply.liked || false;
    const initialLikes = targetReply.likes;
    const newLiked = !wasLiked;
    const newLikes = wasLiked ? initialLikes - 1 : initialLikes + 1;

    // Optimistic update
    setPost(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        replies: prev.replies.map(reply =>
          reply.id === replyId
            ? { ...reply, liked: newLiked, likes: newLikes }
            : reply
        )
      };
    });

    // Emit event to sync across components
    commentEventBus.emitLike(parseInt(replyId), newLiked, newLikes);

    try {
      const response = await fetchWithAuth(`/api/communities/${post.community.id}/posts/${post.id}/replies/${replyId}/like`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      });

      if (!response.ok) {
        // Revert on error
        setPost(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            replies: prev.replies.map(reply =>
              reply.id === replyId
                ? { ...reply, liked: wasLiked, likes: initialLikes }
                : reply
            )
          };
        });
        // Emit revert event
        commentEventBus.emitLike(parseInt(replyId), wasLiked, initialLikes);
      }
    } catch (error) {
      // Revert on error
      setPost(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          replies: prev.replies.map(reply =>
            reply.id === replyId
              ? { ...reply, liked: wasLiked, likes: initialLikes }
              : reply
          )
        };
      });
      // Emit revert event
      commentEventBus.emitLike(parseInt(replyId), wasLiked, initialLikes);
    }
  }, [post, user]);

  const handleCommentDislike = useCallback(async (replyId: string) => {
    if (!post) return;
    if (!user) {
      setShowLoginModal(true);
      return;
    }

    // Find the reply to capture initial state
    const targetReply = post.replies.find(r => r.id === replyId);
    if (!targetReply) return;

    const wasDisliked = targetReply.disliked || false;
    const initialDislikes = targetReply.dislikes;
    const newDisliked = !wasDisliked;
    const newDislikes = wasDisliked ? initialDislikes - 1 : initialDislikes + 1;

    // Optimistic update
    setPost(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        replies: prev.replies.map(reply =>
          reply.id === replyId
            ? { ...reply, disliked: newDisliked, dislikes: newDislikes }
            : reply
        )
      };
    });

    // Emit event to sync across components
    commentEventBus.emitDislike(parseInt(replyId), newDisliked, newDislikes);

    try {
      const response = await fetchWithAuth(`/api/communities/${post.community.id}/posts/${post.id}/replies/${replyId}/dislike`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      });

      if (!response.ok) {
        // Revert on error
        setPost(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            replies: prev.replies.map(reply =>
              reply.id === replyId
                ? { ...reply, disliked: wasDisliked, dislikes: initialDislikes }
                : reply
            )
          };
        });
        // Emit revert event
        commentEventBus.emitDislike(parseInt(replyId), wasDisliked, initialDislikes);
      }
    } catch (error) {
      // Revert on error
      setPost(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          replies: prev.replies.map(reply =>
            reply.id === replyId
              ? { ...reply, disliked: wasDisliked, dislikes: initialDislikes }
              : reply
          )
        };
      });
      // Emit revert event
      commentEventBus.emitDislike(parseInt(replyId), wasDisliked, initialDislikes);
    }
  }, [post, user]);

  const handleSubmitComment = async () => {
    if (!post || !commentText.trim()) return;
    if (!user) {
      setShowLoginModal(true);
      return;
    }

    setIsSubmittingComment(true);
    try {
      const response = await fetchWithAuth(`/api/communities/${post.community.id}/posts/${post.id}/replies`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ content: commentText })
      });

      if (response.ok) {
        setCommentText('');
        // Refresh replies to show the new comment
        fetchReplies(1);
      }
    } catch (error) {
    } finally {
      setIsSubmittingComment(false);
    }
  };

  const handleFollow = async () => {
    if (!post) return;
    if (!user) {
      setShowLoginModal(true);
      return;
    }
    try {
      const endpoint = isFollowing ? 'unfollow' : 'follow';
      const response = await fetchWithAuth(`/api/profile/${post.author.username}/${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      });

      if (response.ok) {
        setIsFollowing(!isFollowing);
      }
    } catch (error) {
    }
  };

  const handleEditPost = useCallback((postToEdit: CommunityPost) => {
    setShowEditModal(true);
    setShowMenu(false);
  }, []);

  const handleEdit = async (updatedData: { title?: string; content: string; mediaUrl?: string; removeMedia?: boolean }) => {
    if (!post || !user) return;

    const permUser = {
      id: user.id,
      username: user.username,
      isAdmin: (user as any).isSiteAdmin || (user as any).isSuperAdmin || false,
      isModerator: (user as any).isSiteModerator || false
    };

    const editorType = getEditorType(post as any, permUser);
    const endpoint = `/api/moderation/communities/${post.community.id}/posts/${post.id}`;

    try {
      const response = await fetch(endpoint, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          ...updatedData,
          moderatorEditReason: editorType !== 'user' ? 'Post edited by moderator' : undefined
        })
      });

      if (response.ok) {
        const updated = await response.json();
        setPost(prev => prev ? { ...prev, ...updatedData } : prev);
        setShowEditModal(false);
      }
    } catch (error) {
      throw error;
    }
  };

  const handlePinPost = useCallback(async () => {
    if (!post || !user) return;

    const permUser = {
      id: user.id,
      username: user.username,
      isAdmin: (user as any).isSiteAdmin || (user as any).isSuperAdmin || false,
      isModerator: (user as any).isSiteModerator || false
    };

    const endpoint = `/api/moderation/communities/${post.community.id}/posts/${post.id}/pin`;

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setPost(prev => prev ? {
          ...prev,
          isPinned: data.pinned
        } : prev);
        setShowMenu(false);
      }
    } catch (error) {
    }
  }, [post, user]);

  const handlePinToFeed = useCallback(async () => {
    if (!post || !user || !(user as any).isSuperAdmin) return;

    try {
      const response = await fetchWithAuth(`/api/moderation/posts/${post.id}/pin-to-feed`, {
        method: 'POST',
        headers: {
        },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setPost(prev => prev ? {
          ...prev,
          isPinnedToFeed: data.pinnedToFeed
        } as CommunityPost : prev);
        setShowMenu(false);
      }
    } catch (error) {
    }
  }, [post, user]);

  const handleDeletePost = useCallback(async () => {
    if (!post || !confirm('Are you sure you want to delete this post?')) return;

    const endpoint = `/api/moderation/communities/${post.community.id}/posts/${post.id}`;

    try {
      const response = await fetch(endpoint, {
        method: 'DELETE',
        headers: {
        },
        credentials: 'include'
      });

      if (response.ok) {
        setShowMenu(false);
        navigate(`/community/${post.community.nameSlug}`);
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to delete post');
      }
    } catch (error) {
      alert('Network error. Please try again.');
    }
  }, [post, navigate]);

  const handleToggleMenu = useCallback(() => {
    setShowMenu(prev => !prev);
  }, []);

  const handleCloseMenu = useCallback(() => {
    setShowMenu(false);
  }, []);

  const handleEditPostClick = useCallback(() => {
    handleEditPost(post!);
  }, [handleEditPost, post]);

  const handlePreviousPage = useCallback(() => {
    fetchReplies(repliesPage - 1);
  }, [fetchReplies, repliesPage]);

  const handleNextPage = useCallback(() => {
    fetchReplies(repliesPage + 1);
  }, [fetchReplies, repliesPage]);

  if (loading) {
    return <LoadingScreen />;
  }

  const isLiked = post?.liked || false;
  const isDisliked = post?.disliked || false;
  const isOwnPost = post?.author.id === user?.id;

  return (
    <div
      className="relative"
      style={{
        minHeight: backgroundHeight,
        background: getBackground(localTheme as any, communityCustomization || {}, theme.appBg)
      }}
    >
      <PostDetailNavbar
        onBack={() => navigate(-1)}
        onLogout={handleLogout}
        currentUser={user}
        notifications={notifications}
        unreadCount={unreadCount}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        theme={localTheme}
        showNotifications={showNotifications}
        setShowNotifications={setShowNotifications}
        showProfileMenu={showProfileMenu}
        setShowProfileMenu={setShowProfileMenu}
      />

      {/* Wavy Line Separator */}
      <div className="w-full flex justify-center py-4">
        <svg width="80%" height="20" viewBox="0 0 800 20" preserveAspectRatio="none">
          <path
            d="M0,10 Q200,0 400,10 T800,10"
            stroke={localTheme.accentColor}
            strokeWidth="3"
            fill="none"
          />
        </svg>
      </div>

      {/* Main Content */}
      <div
        style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          paddingTop: `${scaleH(120, scale.h)}px`,
          paddingBottom: `${scaleH(80, scale.h)}px`
        }}
      >
        {!post ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <h2 style={{ color: localTheme.textColor, fontSize: `${scaleW(24, scale.w)}px`, marginBottom: `${scaleH(16, scale.h)}px` }}>
                Post not found
              </h2>
              <p style={{ color: localTheme.textColor + '99', fontSize: `${scaleW(16, scale.w)}px` }}>
                This post may have been deleted or doesn't exist.
              </p>
            </div>
          </div>
        ) : (
          <div
            style={{
              width: '100%',
              maxWidth: `${scaleW(1200, scale.w)}px`,
              padding: `0 ${scaleW(64, scale.w)}px`
            }}
          >
            {/* Breadcrumbs */}
            <div style={{ marginBottom: `${scaleH(24, scale.h)}px` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: `${scaleW(8, scale.w)}px`, fontSize: `${scaleW(14, scale.w)}px`, color: localTheme.textColor + '99' }}>
                <Link
                  to={`/community/${post.community.nameSlug}`}
                  style={{ color: localTheme.accentColor, textDecoration: 'none' }}
                  onMouseEnter={(e: React.MouseEvent<HTMLElement>) => e.currentTarget.style.textDecoration = 'underline'}
                  onMouseLeave={(e: React.MouseEvent<HTMLElement>) => e.currentTarget.style.textDecoration = 'none'}
                >
                  {post.community.name}
                </Link>
                <span>/</span>
                <span>{post.postType === 'forum' ? 'Forum Post' : 'Community Post'}</span>
              </div>
            </div>

            {/* Post Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              style={{
                background: getCardBackground(localTheme as any, communityCustomization || {}, theme.cardBg),
                borderRadius: `${scaleW(16, scale.w)}px`,
                border: `1px solid ${localTheme.accentColor}40`,
                padding: `${scaleH(32, scale.h)}px`,
                position: 'relative',
                marginBottom: `${scaleH(32, scale.h)}px`
              }}
            >
              <PostBadges post={post as any} textColor={localTheme.textColor} accentColor={localTheme.accentColor} />

              {/* Post Type Badge */}
              {post.postType === 'forum' && (
                <div style={{ marginBottom: `${scaleH(16, scale.h)}px` }}>
                  <span
                    style={{
                      backgroundColor: localTheme.accentColor,
                      color: localTheme.textColor,
                      padding: `${scaleH(4, scale.h)}px ${scaleW(12, scale.w)}px`,
                      borderRadius: `${scaleW(12, scale.w)}px`,
                      fontSize: `${scaleW(12, scale.w)}px`,
                      fontWeight: 600
                    }}
                  >
                    Forum Post
                  </span>
                </div>
              )}

              {/* Author Section */}
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: `${scaleH(24, scale.h)}px` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: `${scaleW(16, scale.w)}px` }}>
                  <Link to={`/user/${post.author.username}`} style={{ textDecoration: 'none' }}>
                    {post.author.profilePicture || post.author.image ? (
                      <img
                        src={resolveAvatar(post.author.profilePicture || post.author.image) || post.author.profilePicture || post.author.image}
                        alt={post.author.profileName || post.author.name || post.author.username}
                        style={{
                          width: `${scaleW(56, scale.w)}px`,
                          height: `${scaleH(56, scale.h)}px`,
                          borderRadius: '50%',
                          objectFit: 'cover'
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: `${scaleW(56, scale.w)}px`,
                          height: `${scaleH(56, scale.h)}px`,
                          borderRadius: '50%',
                          background: `linear-gradient(135deg, ${localTheme.accentColor}, ${localTheme.accentColor}dd)`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: `${scaleW(24, scale.w)}px`,
                          fontWeight: 600,
                          color: localTheme.textColor
                        }}
                      >
                        {post.author ? (post.author.profileName || post.author.name || post.author.username)?.[0]?.toUpperCase() || 'U' : 'U'}
                      </div>
                    )}
                  </Link>

                  <div>
                    <Link
                      to={`/user/${post.author.username}`}
                      style={{
                        fontSize: `${scaleW(18, scale.w)}px`,
                        fontWeight: 600,
                        color: localTheme.textColor,
                        textDecoration: 'none',
                        display: 'block',
                        marginBottom: `${scaleH(2, scale.h)}px`
                      }}
                      onMouseEnter={(e: React.MouseEvent<HTMLElement>) => e.currentTarget.style.textDecoration = 'underline'}
                      onMouseLeave={(e: React.MouseEvent<HTMLElement>) => e.currentTarget.style.textDecoration = 'none'}
                    >
                      {post.author.profileName || post.author.name || post.author.username}
                    </Link>
                    <Link
                      to={`/user/${post.author.username}`}
                      style={{
                        fontSize: `${scaleW(13, scale.w)}px`,
                        color: localTheme.textColor + '99',
                        textDecoration: 'none',
                        display: 'block'
                      }}
                    >
                      @{post.author.username}
                    </Link>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: `${scaleW(12, scale.w)}px` }}>
                  {user && !isOwnPost && (
                    <button
                      onClick={handleFollow}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: `${scaleW(8, scale.w)}px`,
                        padding: `${scaleH(8, scale.h)}px ${scaleW(16, scale.w)}px`,
                        borderRadius: `${scaleW(8, scale.w)}px`,
                        border: `1px solid ${localTheme.accentColor}`,
                        background: isFollowing ? 'transparent' : localTheme.accentColor,
                        color: localTheme.textColor,
                        fontSize: `${scaleW(14, scale.w)}px`,
                        fontWeight: 500,
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                    >
                      {isFollowing ? <UserMinus size={scaleW(16, scale.w)} /> : <UserPlus size={scaleW(16, scale.w)} />}
                      {isFollowing ? 'Unfollow' : 'Follow'}
                    </button>
                  )}

                  {user && canModeratePost(post as any, {
                    id: user.id,
                    username: user.username,
                    isAdmin: (user as any).isSiteAdmin || (user as any).isSuperAdmin || false,
                    isModerator: (user as any).isSiteModerator || false
                  }) && (
                    <div style={{ position: 'relative' }} ref={menuRef}>
                      <button
                        onClick={handleToggleMenu}
                        className="p-1.5 rounded-lg transition-all hover:opacity-80"
                        style={{
                          backgroundColor: localTheme.accentColor + '20',
                          color: localTheme.textColor,
                          border: 'none',
                          cursor: 'pointer'
                        }}
                      >
                        <MoreVertical size={16} />
                      </button>

                      <AnimatePresence>
                        {showMenu && (
                          <>
                            <motion.div
                              initial={{ opacity: 0, scale: 0.95, y: -10 }}
                              animate={{ opacity: 1, scale: 1, y: 0 }}
                              exit={{ opacity: 0, scale: 0.95, y: -10 }}
                              transition={{ duration: 0.15 }}
                              style={{
                                position: 'absolute',
                                top: '100%',
                                right: 0,
                                marginTop: `${scaleH(8, scale.h)}px`,
                                background: themeColors.tabBarBg || 'rgba(30, 58, 95, 0.95)',
                                border: `1px solid ${localTheme.accentColor}60`,
                                borderRadius: `${scaleW(12, scale.w)}px`,
                                padding: `${scaleH(8, scale.h)}px`,
                                minWidth: `${scaleW(140, scale.w)}px`,
                                zIndex: 1001,
                                backdropFilter: 'blur(10px)'
                              }}
                            >
                              <button
                                onClick={handleEditPostClick}
                                className="flex items-center gap-2 w-full px-4 py-3 text-left text-sm font-medium transition-all"
                                style={{
                                  color: localTheme.textColor,
                                  backgroundColor: 'transparent',
                                  border: 'none',
                                  cursor: 'pointer',
                                  borderRadius: `${scaleW(8, scale.w)}px`
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = localTheme.accentColor + '20'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                              >
                                <Edit3 size={16} />
                                Edit
                              </button>
                              <button
                                onClick={handlePinPost}
                                className="flex items-center gap-2 w-full px-4 py-3 text-left text-sm font-medium transition-all"
                                style={{
                                  color: localTheme.textColor,
                                  backgroundColor: 'transparent',
                                  border: 'none',
                                  cursor: 'pointer',
                                  borderRadius: `${scaleW(8, scale.w)}px`
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = localTheme.accentColor + '20'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                              >
                                <Pin size={16} />
                                {post.isPinned ? 'Unpin' : 'Pin'}
                              </button>
                              {(user as any).isSuperAdmin && (
                                <button
                                  onClick={handlePinToFeed}
                                  className="flex items-center gap-2 w-full px-4 py-3 text-left text-sm font-medium transition-all"
                                  style={{
                                    color: localTheme.textColor,
                                    backgroundColor: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                    borderRadius: `${scaleW(8, scale.w)}px`
                                  }}
                                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = localTheme.accentColor + '20'}
                                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                >
                                  <Pin size={16} />
                                  {(post as any).isPinnedToFeed ? 'Unpin from Feed' : 'Pin to Feed'}
                                </button>
                              )}
                              <button
                                onClick={handleDeletePost}
                                className="flex items-center gap-2 w-full px-4 py-3 text-left text-sm font-medium transition-all"
                                style={{
                                  color: '#ef4444',
                                  backgroundColor: 'transparent',
                                  border: 'none',
                                  cursor: 'pointer',
                                  borderRadius: `${scaleW(8, scale.w)}px`
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#ef444420'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                              >
                                <Trash size={16} />
                                Delete
                              </button>
                            </motion.div>
                            <div
                              className="fixed inset-0"
                              style={{ zIndex: 90 }}
                              onClick={handleCloseMenu}
                            />
                          </>
                        )}
                      </AnimatePresence>
                    </div>
                  )}
                </div>
              </div>

              {/* Title */}
              {post.title && (
                <h1 style={{
                  fontSize: `${scaleW(32, scale.w)}px`,
                  fontWeight: 700,
                  color: localTheme.textColor,
                  marginBottom: `${scaleH(16, scale.h)}px`,
                  lineHeight: 1.2
                }}>
                  {post.title}
                </h1>
              )}

              {/* Tags */}
              {post.tags && post.tags.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: `${scaleW(8, scale.w)}px`, marginBottom: `${scaleH(16, scale.h)}px` }}>
                  {post.tags.map((tag, idx) => (
                    <Link
                      key={idx}
                      to={`/tags/${tag}`}
                      style={{
                        backgroundColor: localTheme.accentColor + '20',
                        color: localTheme.accentColor,
                        padding: `${scaleH(4, scale.h)}px ${scaleW(12, scale.w)}px`,
                        borderRadius: `${scaleW(12, scale.w)}px`,
                        fontSize: `${scaleW(12, scale.w)}px`,
                        fontWeight: 500,
                        textDecoration: 'none',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseEnter={(e: React.MouseEvent<HTMLElement>) => e.currentTarget.style.backgroundColor = localTheme.accentColor + '30'}
                      onMouseLeave={(e: React.MouseEvent<HTMLElement>) => e.currentTarget.style.backgroundColor = localTheme.accentColor + '20'}
                    >
                      #{tag}
                    </Link>
                  ))}
                </div>
              )}

              {/* Content */}
              <div style={{
                fontSize: `${scaleW(16, scale.w)}px`,
                lineHeight: 1.6,
                color: localTheme.textColor,
                marginBottom: `${scaleH(24, scale.h)}px`,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }}>
                {post.content}
              </div>

              {/* Timestamp */}
              <div style={{
                fontSize: `${scaleW(14, scale.w)}px`,
                color: localTheme.textColor + '99',
                marginBottom: `${scaleH(24, scale.h)}px`,
                position: 'relative',
                left: '0.5rem'
              }}>
                {new Date(post.createdAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </div>

              {/* Action Buttons */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: `${scaleW(24, scale.w)}px`,
                paddingTop: `${scaleH(24, scale.h)}px`,
                borderTop: `1px solid ${localTheme.accentColor}20`
              }}>
                <button
                  onClick={handleLike}
                  disabled={!user}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: `${scaleW(8, scale.w)}px`,
                    background: 'transparent',
                    border: 'none',
                    color: isLiked ? localTheme.accentColor : localTheme.textColor + 'aa',
                    fontSize: `${scaleW(14, scale.w)}px`,
                    cursor: user ? 'pointer' : 'not-allowed',
                    transition: 'color 0.2s'
                  }}
                  onMouseEnter={(e) => user && (e.currentTarget.style.color = localTheme.accentColor)}
                  onMouseLeave={(e) => !isLiked && (e.currentTarget.style.color = localTheme.textColor + 'aa')}
                >
                  <Heart size={scaleW(20, scale.w)} fill={isLiked ? localTheme.accentColor : 'none'} />
                  {post.likes}
                </button>

                <button
                  onClick={handleDislike}
                  disabled={!user}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: `${scaleW(8, scale.w)}px`,
                    background: 'transparent',
                    border: 'none',
                    color: isDisliked ? '#ef4444' : localTheme.textColor + 'aa',
                    fontSize: `${scaleW(14, scale.w)}px`,
                    cursor: user ? 'pointer' : 'not-allowed',
                    transition: 'color 0.2s'
                  }}
                  onMouseEnter={(e) => user && (e.currentTarget.style.color = '#ef4444')}
                  onMouseLeave={(e) => !isDisliked && (e.currentTarget.style.color = localTheme.textColor + 'aa')}
                >
                  <ThumbsDown size={scaleW(20, scale.w)} fill={isDisliked ? '#ef4444' : 'none'} />
                  {post.dislikes}
                </button>

                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: `${scaleW(8, scale.w)}px`,
                  color: localTheme.textColor + 'aa',
                  fontSize: `${scaleW(14, scale.w)}px`
                }}>
                  <MessageCircle size={scaleW(20, scale.w)} />
                  {post.comments}
                </div>
              </div>
            </motion.div>

            {/* Comments Section */}
            <div style={{ marginTop: `${scaleH(32, scale.h)}px` }}>
              <h2 style={{
                fontSize: `${scaleW(24, scale.w)}px`,
                fontWeight: 600,
                color: localTheme.textColor,
                marginBottom: `${scaleH(24, scale.h)}px`
              }}>
                Comments ({post.replies.length})
              </h2>

              {/* Comment Input */}
              {user && (
                <div style={{
                  background: getCardBackground(localTheme as any, communityCustomization || {}, theme.cardBg),
                  borderRadius: `${scaleW(16, scale.w)}px`,
                  border: `1px solid ${localTheme.accentColor}40`,
                  padding: `${scaleH(24, scale.h)}px`,
                  marginBottom: `${scaleH(24, scale.h)}px`
                }}>
                  <textarea
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    placeholder="Write a comment..."
                    style={{
                      width: '100%',
                      minHeight: `${scaleH(100, scale.h)}px`,
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: `1px solid ${localTheme.accentColor}20`,
                      borderRadius: `${scaleW(12, scale.w)}px`,
                      padding: `${scaleH(16, scale.h)}px`,
                      color: localTheme.textColor,
                      fontSize: `${scaleW(14, scale.w)}px`,
                      resize: 'vertical',
                      fontFamily: 'inherit',
                      outline: 'none',
                      marginBottom: `${scaleH(12, scale.h)}px`
                    }}
                    onFocus={(e) => e.currentTarget.style.borderColor = localTheme.accentColor + '60'}
                    onBlur={(e) => e.currentTarget.style.borderColor = localTheme.accentColor + '20'}
                  />
                  <button
                    onClick={handleSubmitComment}
                    disabled={!commentText.trim() || isSubmittingComment}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: `${scaleW(8, scale.w)}px`,
                      padding: `${scaleH(10, scale.h)}px ${scaleW(20, scale.w)}px`,
                      background: localTheme.accentColor,
                      color: localTheme.textColor,
                      border: 'none',
                      borderRadius: `${scaleW(8, scale.w)}px`,
                      fontSize: `${scaleW(14, scale.w)}px`,
                      fontWeight: 500,
                      cursor: (!commentText.trim() || isSubmittingComment) ? 'not-allowed' : 'pointer',
                      opacity: (!commentText.trim() || isSubmittingComment) ? 0.5 : 1,
                      transition: 'opacity 0.2s'
                    }}
                  >
                    <Send size={scaleW(16, scale.w)} />
                    {isSubmittingComment ? 'Posting...' : 'Post Comment'}
                  </button>
                </div>
              )}

              {/* Comments List */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: `${scaleH(16, scale.h)}px` }}>
                {post.replies.map(reply => {
                  const replyLiked = reply.liked || false;
                  const replyDisliked = reply.disliked || false;

                  return (
                    <motion.div
                      key={reply.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      style={{
                        background: getCardBackground(localTheme as any, communityCustomization || {}, theme.cardBg),
                        borderRadius: `${scaleW(12, scale.w)}px`,
                        border: `1px solid ${localTheme.accentColor}20`,
                        padding: `${scaleH(20, scale.h)}px`
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: `${scaleW(12, scale.w)}px` }}>
                        <Link to={`/user/${reply.author.username}`}>
                          {reply.author.profilePicture || reply.author.image ? (
                            <img
                              src={resolveAvatar(reply.author.profilePicture || reply.author.image) || reply.author.profilePicture || reply.author.image}
                              alt={reply.author.profileName || reply.author.name || reply.author.username}
                              style={{
                                width: `${scaleW(40, scale.w)}px`,
                                height: `${scaleH(40, scale.h)}px`,
                                borderRadius: '50%',
                                objectFit: 'cover'
                              }}
                            />
                          ) : (
                            <div
                              style={{
                                width: `${scaleW(40, scale.w)}px`,
                                height: `${scaleH(40, scale.h)}px`,
                                borderRadius: '50%',
                                background: `linear-gradient(135deg, ${localTheme.accentColor}, ${localTheme.accentColor}dd)`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: `${scaleW(16, scale.w)}px`,
                                fontWeight: 600,
                                color: localTheme.textColor
                              }}
                            >
                              {reply.author ? (reply.author.profileName || reply.author.name || reply.author.username)?.[0]?.toUpperCase() || 'U' : 'U'}
                            </div>
                          )}
                        </Link>

                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: `${scaleH(2, scale.h)}px`, marginBottom: `${scaleH(8, scale.h)}px` }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: `${scaleW(8, scale.w)}px` }}>
                              <Link
                                to={`/user/${reply.author.username}`}
                                style={{
                                  fontSize: `${scaleW(14, scale.w)}px`,
                                  fontWeight: 600,
                                  color: localTheme.textColor,
                                  textDecoration: 'none'
                                }}
                                onMouseEnter={(e: React.MouseEvent<HTMLElement>) => e.currentTarget.style.textDecoration = 'underline'}
                                onMouseLeave={(e: React.MouseEvent<HTMLElement>) => e.currentTarget.style.textDecoration = 'none'}
                              >
                                {reply.author.profileName || reply.author.name || reply.author.username}
                              </Link>
                              <span style={{ fontSize: `${scaleW(12, scale.w)}px`, color: localTheme.textColor + '66' }}>·</span>
                              <span style={{ fontSize: `${scaleW(12, scale.w)}px`, color: localTheme.textColor + '66' }}>
                                {new Date(reply.createdAt).toLocaleDateString()}
                              </span>
                            </div>
                            <Link
                              to={`/user/${reply.author.username}`}
                              style={{
                                fontSize: `${scaleW(12, scale.w)}px`,
                                color: localTheme.textColor + '99',
                                textDecoration: 'none'
                              }}
                            >
                              @{reply.author.username}
                            </Link>
                          </div>

                          <p style={{
                            fontSize: `${scaleW(14, scale.w)}px`,
                            color: localTheme.textColor + 'e6',
                            lineHeight: 1.5,
                            marginBottom: `${scaleH(12, scale.h)}px`,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word'
                          }}>
                            {reply.content}
                          </p>

                          <div style={{ display: 'flex', alignItems: 'center', gap: `${scaleW(16, scale.w)}px` }}>
                            <button
                              onClick={() => handleCommentLike(reply.id)}
                              disabled={!user}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: `${scaleW(6, scale.w)}px`,
                                background: 'transparent',
                                border: 'none',
                                color: replyLiked ? localTheme.accentColor : localTheme.textColor + '99',
                                fontSize: `${scaleW(12, scale.w)}px`,
                                cursor: user ? 'pointer' : 'not-allowed',
                                transition: 'color 0.2s'
                              }}
                              onMouseEnter={(e) => user && (e.currentTarget.style.color = localTheme.accentColor)}
                              onMouseLeave={(e) => !replyLiked && (e.currentTarget.style.color = localTheme.textColor + '99')}
                            >
                              <ThumbsUp size={scaleW(14, scale.w)} fill={replyLiked ? localTheme.accentColor : 'none'} />
                              {reply.likes}
                            </button>

                            <button
                              onClick={() => handleCommentDislike(reply.id)}
                              disabled={!user}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: `${scaleW(6, scale.w)}px`,
                                background: 'transparent',
                                border: 'none',
                                color: replyDisliked ? '#ef4444' : localTheme.textColor + '99',
                                fontSize: `${scaleW(12, scale.w)}px`,
                                cursor: user ? 'pointer' : 'not-allowed',
                                transition: 'color 0.2s'
                              }}
                              onMouseEnter={(e) => user && (e.currentTarget.style.color = '#ef4444')}
                              onMouseLeave={(e) => !replyDisliked && (e.currentTarget.style.color = localTheme.textColor + '99')}
                            >
                              <ThumbsDown size={scaleW(14, scale.w)} fill={replyDisliked ? '#ef4444' : 'none'} />
                              {reply.dislikes}
                            </button>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>

              {/* Pagination Controls */}
              {repliesTotalPages > 1 && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: `${scaleW(12, scale.w)}px`,
                  marginTop: `${scaleH(24, scale.h)}px`,
                  padding: `${scaleH(16, scale.h)}px`
                }}>
                  <button
                    onClick={handlePreviousPage}
                    disabled={repliesPage === 1 || repliesLoading}
                    style={{
                      padding: `${scaleH(8, scale.h)}px ${scaleW(16, scale.w)}px`,
                      background: repliesPage === 1 ? localTheme.cardBg + '40' : localTheme.accentColor,
                      color: localTheme.textColor,
                      border: 'none',
                      borderRadius: `${scaleW(8, scale.w)}px`,
                      fontSize: `${scaleW(14, scale.w)}px`,
                      fontWeight: 500,
                      cursor: repliesPage === 1 || repliesLoading ? 'not-allowed' : 'pointer',
                      opacity: repliesPage === 1 || repliesLoading ? 0.5 : 1,
                      transition: 'all 0.2s'
                    }}
                  >
                    Previous
                  </button>
                  <span style={{
                    fontSize: `${scaleW(14, scale.w)}px`,
                    color: localTheme.textColor,
                    fontWeight: 500
                  }}>
                    {repliesLoading ? 'Loading...' : `Page ${repliesPage} of ${repliesTotalPages}`}
                  </span>
                  <button
                    onClick={handleNextPage}
                    disabled={repliesPage === repliesTotalPages || repliesLoading}
                    style={{
                      padding: `${scaleH(8, scale.h)}px ${scaleW(16, scale.w)}px`,
                      background: repliesPage === repliesTotalPages ? localTheme.cardBg + '40' : localTheme.accentColor,
                      color: localTheme.textColor,
                      border: 'none',
                      borderRadius: `${scaleW(8, scale.w)}px`,
                      fontSize: `${scaleW(14, scale.w)}px`,
                      fontWeight: 500,
                      cursor: repliesPage === repliesTotalPages || repliesLoading ? 'not-allowed' : 'pointer',
                      opacity: repliesPage === repliesTotalPages || repliesLoading ? 0.5 : 1,
                      transition: 'all 0.2s'
                    }}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {post && (
        <EditPostModal
          post={post as any}
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSave={handleEdit}
        />
      )}

      {/* Site Theme Customizer */}
      {showSiteCustomizer && user && (
        <SiteThemeCustomizer
          isOpen={showSiteCustomizer}
          onClose={() => setShowSiteCustomizer(false)}
          userId={user.id.toString()}
          initialAnimations={themeAnimations}
        />
      )}

    </div>
  );
}
